<template>
  <div class="attendance-work-stats-panel">
    <div class="panel-toolbar">
      <el-form :inline="true" size="medium" :model="searchCondition">
        <el-form-item>
          <el-date-picker
            v-model="searchCondition.start_date"
            type="date"
            value-format="yyyy-MM-dd"
            placeholder="开始日期"
            clearable
            style="width: 140px;"
          />
        </el-form-item>
        <el-form-item>
          <el-date-picker
            v-model="searchCondition.end_date"
            type="date"
            value-format="yyyy-MM-dd"
            placeholder="结束日期"
            clearable
            style="width: 140px;"
          />
        </el-form-item>
        <el-form-item>
          <el-select
            v-model="searchCondition.selected_mission_id"
            placeholder="全部任务"
            clearable
            filterable
            :disabled="!!contextMissionId"
            style="width: 180px;"
          >
            <el-option
              v-for="item in missionOptions"
              :key="item.mission_id"
              :label="item.mission_name"
              :value="item.mission_id"
            />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="handleReset">重置</el-button>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" :loading="exportLoading" @click="handleExport">导出</el-button>
        </el-form-item>
      </el-form>
    </div>

    <div v-if="contextMissionName" class="context-mission">
      当前任务：{{ contextMissionName }}
    </div>

    <div class="panel-total">
      总工时：<span class="panel-total__value">{{ totalHours }}</span>(h)
    </div>

    <div class="mode-tabs">
      <div
        v-for="item in modeTabs"
        :key="item.value"
        :class="activeGroupType === item.value ? 'mode-tab active' : 'mode-tab'"
        @click="handleGroupTypeChange(item.value)"
      >
        {{ item.label }}
      </div>
    </div>

    <el-table
      :data="tableList"
      border
      size="small"
      v-loading="loadingStatus"
      class="stats-table"
      header-cell-class-name="stats-table__header"
    >
      <el-table-column prop="mission_name" label="任务名称" min-width="120" fixed="left" />
      <el-table-column
        v-for="item in tableColumns"
        :key="item.key"
        :label="item.title"
        min-width="72"
        align="center"
      >
        <template slot-scope="scope">
          {{ scope.row.bucket_hours && scope.row.bucket_hours[item.key] ? scope.row.bucket_hours[item.key] : "0" }}
        </template>
      </el-table-column>
      <el-table-column prop="row_total_hours" label="合计工时(h)" min-width="100" align="center" />
    </el-table>

    <div class="pagination-wrap">
      <el-pagination
        background
        layout="total, sizes, prev, pager, next, jumper"
        :current-page="paging.currentPage"
        :page-sizes="[10, 20, 50]"
        :page-size="paging.pageSize"
        :total="paging.total"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>
  </div>
</template>

<script>
function pad2(value) {
  return String(value).padStart(2, "0");
}

function monthRange(monthStr) {
  const [year, month] = String(monthStr).split("-");
  const lastDay = new Date(Number(year), Number(month), 0).getDate();
  return {
    start_date: `${year}-${month}-01`,
    end_date: `${year}-${month}-${pad2(lastDay)}`,
  };
}

export default {
  name: "AttendanceWorkStatsPanel",
  props: {
    contextMissionId: {
      type: String,
      default: "",
    },
    contextMissionName: {
      type: String,
      default: "",
    },
    initialMonth: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      loadingStatus: false,
      exportLoading: false,
      activeGroupType: 1,
      modeTabs: [
        { label: "日", value: 1 },
        { label: "周", value: 2 },
        { label: "月", value: 3 },
      ],
      missionOptions: [],
      searchCondition: {
        start_date: "",
        end_date: "",
        selected_mission_id: "",
      },
      totalHours: "0",
      tableColumns: [],
      tableList: [],
      paging: {
        currentPage: 1,
        pageSize: 20,
        total: 0,
      },
    };
  },
  watch: {
    contextMissionId() {
      this.loadInitData();
    },
    initialMonth() {
      this.loadInitData();
    },
  },
  mounted() {
    this.loadInitData();
  },
  methods: {
    getMissionIds() {
      if (this.contextMissionId) return [this.contextMissionId];
      return this.searchCondition.selected_mission_id
        ? [this.searchCondition.selected_mission_id]
        : [];
    },
    getListParams() {
      return {
        mission_ids: this.getMissionIds(),
        start_date: this.searchCondition.start_date || undefined,
        end_date: this.searchCondition.end_date || undefined,
        group_type: this.activeGroupType,
        current_page: this.paging.currentPage,
        page_size: this.paging.pageSize,
      };
    },
    applyInitialFilter(initData = {}) {
      const defaultFilter = initData.default_filter || {};
      const dateScope = this.initialMonth && /^\d{4}-\d{2}$/.test(this.initialMonth)
        ? monthRange(this.initialMonth)
        : null;
      this.searchCondition.start_date =
        (dateScope && dateScope.start_date) || defaultFilter.start_date || "";
      this.searchCondition.end_date =
        (dateScope && dateScope.end_date) || defaultFilter.end_date || "";
      if (this.contextMissionId) {
        this.searchCondition.selected_mission_id = this.contextMissionId;
      } else if (Array.isArray(defaultFilter.mission_ids) && defaultFilter.mission_ids.length) {
        this.searchCondition.selected_mission_id = String(defaultFilter.mission_ids[0]);
      } else {
        this.searchCondition.selected_mission_id = "";
      }
    },
    normalizeMissionOptions(options = []) {
      return options.map((item) => ({
        mission_id: String(item.mission_id || item.id || ""),
        mission_name: item.mission_name || item.name || "",
      })).filter((item) => item.mission_id && item.mission_name);
    },
    async loadInitData() {
      this.loadingStatus = true;
      try {
        const res = await this.$api.checkInWorkHoursInit({
          mission_ids: this.contextMissionId ? [this.contextMissionId] : [],
        });
        this.missionOptions = this.normalizeMissionOptions(res.mission_options || []);
        this.applyInitialFilter(res);
        this.totalHours =
          (res.summary && res.summary.total_work_hours) != null
            ? String(res.summary.total_work_hours)
            : "0";
        this.paging.currentPage = 1;
        await this.fetchList();
      } catch (e) {
        this.missionOptions = [];
        this.tableColumns = [];
        this.tableList = [];
        this.totalHours = "0";
        const msg =
          (e && e.data && e.data.message) || (e && e.msg) || e.message || "工时统计初始化失败";
        this.$message.error(msg);
      } finally {
        this.loadingStatus = false;
      }
    },
    async fetchList() {
      this.loadingStatus = true;
      try {
        const res = await this.$api.checkInWorkHoursList(this.getListParams());
        const summary = res.summary || {};
        const pages = res.pages || {};
        this.totalHours =
          summary.total_work_hours != null ? String(summary.total_work_hours) : "0";
        this.tableColumns = Array.isArray(res.columns) ? res.columns : [];
        this.tableList = Array.isArray(res.list) ? res.list : [];
        this.paging.total = pages.total || 0;
        this.paging.currentPage = pages.current_page || this.paging.currentPage;
      } catch (e) {
        this.tableColumns = [];
        this.tableList = [];
        this.paging.total = 0;
        const msg =
          (e && e.data && e.data.message) || (e && e.msg) || e.message || "工时统计加载失败";
        this.$message.error(msg);
      } finally {
        this.loadingStatus = false;
      }
    },
    handleGroupTypeChange(value) {
      if (this.activeGroupType === value) return;
      this.activeGroupType = value;
      this.paging.currentPage = 1;
      this.fetchList();
    },
    handleSearch() {
      this.paging.currentPage = 1;
      this.fetchList();
    },
    handleReset() {
      this.loadInitData();
    },
    handleSizeChange(size) {
      this.paging.pageSize = size;
      this.paging.currentPage = 1;
      this.fetchList();
    },
    handleCurrentChange(page) {
      this.paging.currentPage = page;
      this.fetchList();
    },
    async handleExport() {
      this.exportLoading = true;
      try {
        const data = await this.$api.checkInWorkHoursExport({
          mission_ids: this.getMissionIds(),
          start_date: this.searchCondition.start_date || undefined,
          end_date: this.searchCondition.end_date || undefined,
          group_type: this.activeGroupType,
        });
        const d = data && typeof data === "object" ? data : {};
        const header = d.header;
        const rows = d.rows;
        if (!header || !rows || !rows.length) {
          this.$message.warning("暂无数据可导出");
          return;
        }
        const { export_json_to_excel } = await import("@/excel/Export2Excel");
        export_json_to_excel({
          header,
          data: rows,
          filename: `工时统计_${this.searchCondition.start_date || "全部"}`,
          autoWidth: true,
          bookType: "xlsx",
        });
        this.$message.success("导出成功");
      } catch (e) {
        const msg =
          (e && e.data && e.data.message) || (e && e.msg) || e.message || "导出失败";
        this.$message.error(msg);
      } finally {
        this.exportLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.attendance-work-stats-panel {
  .context-mission {
    margin-bottom: 12px;
    font-size: 14px;
    color: #606266;
  }

  .panel-toolbar {
    margin-bottom: 18px;
  }

  .panel-total {
    margin-bottom: 14px;
    font-size: 24px;
    font-weight: 600;
    color: #303133;
  }

  .panel-total__value {
    color: #303133;
  }

  .mode-tabs {
    display: inline-flex;
    margin-bottom: 14px;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    overflow: hidden;
  }

  .mode-tab {
    min-width: 70px;
    height: 32px;
    padding: 0 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-right: 1px solid #dcdfe6;
    background: #fff;
    color: #606266;
    cursor: pointer;

    &:last-child {
      border-right: 0;
    }

    &.active {
      background: #1eb4f1;
      color: #fff;
    }
  }

  .stats-table {
    margin-bottom: 16px;
  }

  .pagination-wrap {
    display: flex;
    justify-content: flex-end;
  }
}
</style>
